import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  isXcarchiveEligibleApp,
  isXcarchiveFileName,
  XCARCHIVE_FILE_EXTENSION,
} from 'src/app/Utils/xcarchive.util';

const IOSkey = 'ios';
const IOSFile = '.ipa';
const AndroidFile = '.aab';
const IOSSymbol = '.zip';
const AndroidSymbol = '.txt';

@Component({
  selector: 'app-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrls: ['./upload-file.component.scss'],
})
export class UploadFileComponent {
  @Input() tech: string;
  @Input() appType: string;
  @Output() emitFile: EventEmitter<File[]> = new EventEmitter<File[]>();
  file: File;
  symbol: File;
  aabFileIsInvalid: boolean;
  symbolFileIsInvalid: boolean;
  invalidFileNameCharacter: string;

  constructor(
    private objMatDialog: MatDialog,
    private translate: TranslateService,
  ) {}

  uploadFile(event) {
    this.aabFileIsInvalid = false;
    const newFile: File = event[0];
    this.invalidFileNameCharacter = this.containsForbiddenCharacters(
      newFile.name,
    );
    if (!this.checkFileExtension(newFile) || !!this.invalidFileNameCharacter) {
      this.aabFileIsInvalid = true;
      return;
    }
    this.file = newFile;

    // An xcarchive already contains its own symbols, so any previously
    // selected symbol file no longer applies once the Symbols Frame hides.
    if (this.isBinaryAnXcarchive()) {
      this.resetSymbol();
    }
  }

  uploadSymbol(event) {
    this.symbolFileIsInvalid = false;
    const newFile: File = event[0];
    this.invalidFileNameCharacter = this.containsForbiddenCharacters(
      newFile.name,
    );
    if (
      !this.checkSymbolExtension(newFile) ||
      !!this.invalidFileNameCharacter
    ) {
      this.symbolFileIsInvalid = true;
      return;
    }
    this.symbol = newFile;
  }

  containsForbiddenCharacters(input: string) {
    const forbiddenCharacters = ['/', '\\', '?', '*', '|', '"', '<', '>', ':'];
    for (const char of input) {
      if (forbiddenCharacters.includes(char)) {
        return char;
      }
    }
    return null;
  }

  defineUploadFileErrorMessage(symbolFile: boolean) {
    if (this.invalidFileNameCharacter) {
      return this.translate.instant(
        'component.common.upload-file-forbidden-character-error-message',
        { character: this.invalidFileNameCharacter },
      );
    }

    if (!symbolFile && this.isXcarchiveEligible()) {
      return this.translate.instant(
        'component.common.upload-file-ipa-or-xcarchive-error-message',
      );
    }

    const extension = symbolFile ? this.getSymbolType() : this.getFileType();
    return this.translate.instant(
      'component.common.upload-file-extension-error-message',
      { extension },
    );
  }

  isXcarchiveEligible(): boolean {
    return isXcarchiveEligibleApp(this.tech, this.appType);
  }

  isBinaryAnXcarchive(): boolean {
    return isXcarchiveFileName(this.tech, this.appType, this.file?.name);
  }

  showSymbolStep(): boolean {
    if (!this.isXcarchiveEligible()) {
      return true;
    }
    return !!this.file && !this.isBinaryAnXcarchive();
  }

  getFileType(): string {
    if (this.tech !== IOSkey) {
      return AndroidFile;
    }
    return this.isXcarchiveEligible()
      ? `${IOSFile} / ${XCARCHIVE_FILE_EXTENSION}`
      : IOSFile;
  }

  getSymbolType(): string {
    return this.tech === IOSkey ? IOSSymbol : AndroidSymbol;
  }

  defineBinaryHeaderText(): string {
    return this.isXcarchiveEligible()
      ? 'component.common.upload-file-binary-header'
      : 'component.common.file-header';
  }

  defineBinaryDropText(): string {
    return this.isXcarchiveEligible()
      ? 'component.common.upload-file-binary-drop'
      : 'component.common.drop';
  }

  defineFileHeaderText() {
    return this.getSymbolType() === AndroidSymbol
      ? 'component.common.android-symbol-file-header'
      : 'component.appdistribution.create-version.symbol-file-header';
  }

  showSymbolsHelperMessage(): boolean {
    return this.isXcarchiveEligible();
  }

  defineSymbolsHelperMessage(): string {
    return this.translate.instant(
      'component.common.upload-file-symbols-helper-message',
    );
  }

  uploadBothFile() {
    this.emitFile.emit([this.file, this.symbol]);
  }

  resetFile() {
    this.file = undefined;
  }

  resetSymbol() {
    this.symbol = undefined;
  }

  disableSubmitBtn() {
    return !this.file;
  }

  isIosApp() {
    return this.tech === IOSkey;
  }

  defineHelperMessage() {
    let key = 'component.common.upload-file-android-helper-message';
    if (this.isXcarchiveEligible()) {
      key = 'component.common.upload-file-ios-binary-helper-message';
    } else if (this.isIosApp()) {
      key = 'component.common.upload-file-ios-helper-message';
    }
    return this.translate.instant(key);
  }

  private checkFileExtension(file: File): boolean {
    const name = file?.name?.toLowerCase();
    if (!name) {
      return false;
    }
    if (this.isXcarchiveEligible()) {
      return (
        name.endsWith(IOSFile) ||
        isXcarchiveFileName(this.tech, this.appType, name)
      );
    }
    return name.endsWith(this.getFileType());
  }

  private checkSymbolExtension(file: File): boolean {
    return file?.name?.toLowerCase().endsWith(this.getSymbolType());
  }
}
